'use client'

import NavLink from '@/components/ui/NavLink'
import AppIcon from '@/components/content/app/AppIcon'
import Badge from '@/components/ui/Badge'
import { getBrandColors, toSlug } from '@/lib/utils'
import { useCoursesMenuContext } from '@/context/NavbarContext'
import { checkAppBranding } from '@/lib/formatting'
import type { Course, App, ProductCourse } from '@/payload-types'

export default function CoursesMenu() {
  const { courses } = useCoursesMenuContext()

  return (
    <div data-menu="Courses" className="flex w-full shrink navigation:w-[400px]">
      <div className="flex w-full flex-col gap-2 overflow-hidden p-2 navigation:gap-5">
        {courses.length > 0 ? (
          courses.map((course) => <CoursePreview key={course.id} course={course} />)
        ) : (
          <span className="pt-1 text-center text-13 text-gray-500">No courses found</span>
        )}
      </div>
    </div>
  )
}

export function CoursePreview({ course }: { course: Course }) {
  const app = course?.app as App | undefined

  if (!app) {
    console.error('course.app not populated')
    return null
  }

  const productCourse = course.productCourse as ProductCourse

  const { appGlyph, appIcon } = checkAppBranding(app)
  const { backgroundColor } = getBrandColors(app)

  const time = course.duration

  return (
    <NavLink
      href={`/courses/${app.slug || (app.name ? toSlug(app.name) : '')}`}
      className="group flex h-16 items-center gap-5 rounded-xl px-4 transition-colors duration-300 hover:bg-gray-100 dark:hover:bg-white/10"
    >
      <AppIcon
        icon={appIcon}
        glyph={appGlyph}
        name={app.name}
        background={backgroundColor}
        size={40}
      />
      <div className="flex w-full flex-col gap-2 truncate">
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-14 font-medium leading-none text-gray-950 transition-colors duration-300 group-hover:text-primary-500 dark:text-white">
            {course.name}
          </span>
          {time && (
            <Badge
              count={time}
              className="cursor-pointer text-gray-400"
              icon="circle-half-dotted-clock"
              nested
            />
          )}
          {course.moduleCount && course.moduleCount > 0 && (
            <Badge nested>{course.moduleCount} modules</Badge>
          )}
        </div>
        <span className="w-full truncate whitespace-nowrap text-12 text-zinc-500">
          {app.description}
        </span>
      </div>
    </NavLink>
  )
}
